import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

interface BrowserClerkOrganization {
  id?: string | null
  slug?: string | null
}

interface BrowserClerk {
  organization?: BrowserClerkOrganization | null
  session?: {
    getToken: (options?: {
      organizationId?: string
      skipCache?: boolean
    }) => Promise<string | null>
  } | null
  user?: {
    organizationMemberships?:
      | { organization: BrowserClerkOrganization }[]
      | null
  } | null
}

/**
 * Reads the organization claims from a Clerk session token.
 *
 * Transport verifies its own tokens the same way in `transportAuthToken`, but
 * module structure keeps `@/transport` out of implementation lanes, so this
 * lane checks its own rather than importing that one.
 */
function readTokenOrganization(
  token: string,
): { id: string | null; slug: string | null } | null {
  const payload = token.split(".")[1]
  if (!payload) {
    return null
  }

  try {
    const claims = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>
    const id = claims.org_id
    const slug = claims.org_slug
    return {
      id: typeof id === "string" ? id : null,
      slug: typeof slug === "string" ? slug : null,
    }
  } catch {
    // An opaque or malformed token cannot be checked here, so it is not
    // rejected on that basis; the server still validates it.
    return null
  }
}

function tokenMatchesOrganization(
  token: string,
  organizationId: string,
  organizationSlug: string,
): boolean {
  const claims = readTokenOrganization(token)
  if (claims === null) {
    return true
  }
  if (claims.id !== null && claims.id !== organizationId) {
    return false
  }
  return claims.slug === null || claims.slug === organizationSlug
}

/**
 * Mints a session token for the organization the tab's URL names.
 *
 * A bare `getToken()` signs for whichever organization the tab last activated,
 * which is only incidentally the one the page is showing — a deep link, or a
 * route reached before activation settled, produces a token for the wrong
 * organization. The route organization is resolved against the user's own
 * memberships so the token is tied to the URL instead.
 *
 * Passing `organizationId` is not enough on its own: during a Clerk state
 * transition the cache can return a token minted for the previous
 * organization. The returned claims are compared against the route
 * organization and the mint retried once with the cache skipped.
 *
 * Returns null when the route organization cannot be resolved, or when the
 * minted token keeps disagreeing with it. Every caller throws on a null token,
 * so the request fails rather than being authorized for the wrong organization.
 */
export async function getActiveClerkToken(): Promise<string | null> {
  if (typeof window === "undefined") {
    return null
  }

  const clerk = (window as typeof window & { Clerk?: BrowserClerk }).Clerk
  const session = clerk?.session
  if (!session) {
    return null
  }

  const routeOrganizationSlug = getRouteOrganizationSlug(
    window.location.pathname,
  )
  if (routeOrganizationSlug === null) {
    return session.getToken()
  }

  const activeOrganization = clerk?.organization
  const organizationId =
    activeOrganization?.slug === routeOrganizationSlug
      ? (activeOrganization?.id ?? null)
      : (clerk?.user?.organizationMemberships?.find(
          (membership) =>
            membership.organization.slug === routeOrganizationSlug,
        )?.organization.id ?? null)

  if (organizationId === null) {
    return null
  }

  for (const skipCache of [false, true]) {
    const token = await session.getToken({
      organizationId,
      ...(skipCache ? { skipCache } : {}),
    })
    if (
      token &&
      tokenMatchesOrganization(token, organizationId, routeOrganizationSlug)
    ) {
      return token
    }
  }

  return null
}
